import { ReactNode } from 'react';

import { Link } from '@backstage/core-components';
import { useRouteRef } from '@backstage/core-plugin-api';

import {
  type ResourceDisplayNameProps,
  createResourceLinkExtension,
  extractLastNameComponent,
  isResourceName,
} from '@spotify/confidence-core-react';
import {
  PulseV1PulseView,
  getTypeOrNull,
  usePulseBlockQuery,
  usePulseQuery,
} from '@spotify/confidence-graphql';

import { confidencePulsePlugin } from '../plugin';
import { pulsesPageRouteRef } from '../routes';

interface PulseResourceLinkProps {
  name: string;
  text: ReactNode;
  className?: string;
}

const getPulseResourceParts = (
  name: string,
): { pulseName?: string; isBlock: boolean } => {
  const [collection, pulseId, childCollection, childId, ...rest] =
    name.split('/');
  const isPulse = collection === 'pulses' && Boolean(pulseId);
  const isBlock =
    isPulse &&
    childCollection === 'blocks' &&
    Boolean(childId) &&
    rest.length === 0;

  return {
    pulseName: isPulse ? `pulses/${pulseId}` : undefined,
    isBlock,
  };
};

const PulseResourceLink = ({
  name,
  text,
  className,
}: PulseResourceLinkProps) => {
  const pulsesPage = useRouteRef(pulsesPageRouteRef);
  const { pulseName } = getPulseResourceParts(name);
  const pulseId = extractLastNameComponent(pulseName);

  if (!pulsesPage || !pulseId) {
    return <>{text}</>;
  }

  const to = `${pulsesPage()}/${pulseId}`;

  return (
    <Link to={to} className={className} data-track-source-anchor="d6c5aec94a;pulse-resource-link">
      {text}
    </Link>
  );
};

const PulseResourceDisplayName = ({
  name,
  children,
}: ResourceDisplayNameProps) => {
  const { isBlock, pulseName } = getPulseResourceParts(name);
  const { data: pulseData } = usePulseQuery({
    variables: {
      name: pulseName ?? '',
      view: PulseV1PulseView.PulseViewBasic,
    },
    skip: !pulseName || isBlock,
  });
  const { data: pulseBlockData } = usePulseBlockQuery({
    variables: { name },
    skip: !isBlock,
  });
  const pulse = getTypeOrNull(pulseData?.pulse);
  const pulseBlock = getTypeOrNull(pulseBlockData?.pulseBlock);

  return <>{pulseBlock?.displayName || pulse?.displayName || children}</>;
};

export const PulseResourceLinkExtension = confidencePulsePlugin.provide(
  createResourceLinkExtension({
    id: 'pulse-resource-link',
    predicate: ({ name }) => isResourceName(name, 'pulses'),
    DisplayName: PulseResourceDisplayName,
    render: ({ name, text, className }) => (
      <PulseResourceLink name={name} text={text} className={className}  data-track-source-anchor="703b5c766d;pulse-resource-link-extension-pulse-resource"/>
    ),
  }),
);
